import { CubeTransparentIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import type { CbctProcessingPhase } from '../../files/useCbctProcessing';
import CbctProcessingStepper from './CbctProcessingStepper';

export interface CbctProcessingGateProps {
    fileName: string;
    phase: CbctProcessingPhase;
    /** Failure reason, shown when phase is 'failed'. */
    error?: string;
    onProcess: (password?: string) => void;
}

const BUSY_PHASES: ReadonlyArray<CbctProcessingPhase> = ['queued', 'running', 'succeeded'];

const PasswordInput: React.FC<{
    value: string;
    onChange: (value: string) => void;
}> = ({ value, onChange }) => (
    <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Archive password"
        autoComplete="off"
        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
    />
);

/** Replaces the CBCT viewer for scans without manifest/binary refs. */
const CbctProcessingGate: React.FC<CbctProcessingGateProps> = ({
    fileName,
    phase,
    error,
    onProcess
}) => {
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    if (phase === 'checking') {
        return (
            <div className="flex items-center justify-center w-full h-full bg-gray-50 rounded-lg">
                <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
            </div>
        );
    }

    if (BUSY_PHASES.includes(phase)) {
        return (
            <div className="flex flex-col items-center justify-center w-full h-full bg-gray-50 rounded-lg p-8 text-center">
                <CbctProcessingStepper phase={phase} />
                <p className="text-sm text-gray-500 mt-6 max-w-sm">
                    This can take a few minutes. The scan opens automatically once it&apos;s ready.
                    You can safely go back to the files in the meantime.
                </p>
            </div>
        );
    }

    const isFailed = phase === 'failed';

    return (
        <div className="flex flex-col items-center justify-center w-full h-full bg-gray-50 rounded-lg p-8 text-center">
            {isFailed ? (
                <div className="mb-5 w-full flex justify-center">
                    <CbctProcessingStepper phase={phase} />
                </div>
            ) : (
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <CubeTransparentIcon className="w-6 h-6 text-gray-400" />
                </div>
            )}
            <p className="text-sm font-semibold text-gray-800">
                {isFailed ? 'Processing failed' : 'This CBCT scan hasn’t been processed yet'}
            </p>
            {isFailed ? (
                <div className="mt-3 w-full max-w-sm bg-red-50 border border-red-300 rounded-lg px-4 py-3 text-left">
                    <p className="text-sm text-red-800 break-words">
                        {error || 'Something went wrong while processing the scan.'}
                    </p>
                    <p className="text-xs text-red-800 opacity-75 mt-1">
                        If the archive is password protected, enter the password below and retry.
                    </p>
                </div>
            ) : (
                <p className="text-sm text-gray-500 mt-1 max-w-sm">
                    “{fileName}” needs to be processed before it can be opened in the viewer.
                </p>
            )}

            <div className="w-full max-w-xs mt-5 flex flex-col items-center gap-3">
                {isFailed || showPassword ? (
                    <PasswordInput value={password} onChange={setPassword} />
                ) : (
                    <button
                        onClick={() => setShowPassword(true)}
                        className="text-xs font-medium text-gray-500 hover:text-gray-800 underline transition-colors"
                    >
                        Password protected?
                    </button>
                )}
                <button
                    onClick={() => onProcess(password || undefined)}
                    className="bg-black text-white font-semibold px-6 py-2 rounded-xl shadow hover:bg-gray-900 transition-colors"
                >
                    {isFailed ? 'Retry' : 'Process scan'}
                </button>
            </div>
        </div>
    );
};

export default CbctProcessingGate;
